import { Component, Input, Output, EventEmitter, forwardRef, ViewChild, ElementRef, HostBinding } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type InputType = 'text' | 'email' | 'password' | 'url' | 'number' | 'tel' | 'search' | 'date' | 'time' | 'datetime-local';

@Component({
  selector: 'kt-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InputComponent), multi: true }]
})
export class InputComponent implements ControlValueAccessor {
  @Input() type: InputType = 'text';
  @Input() label = '';
  @Input() labelPosition: 'top' | 'left' | 'right' | 'bottom' = 'top';
  /** When set, clamps the label to this many lines and reserves that height, so labels of
   * differing length stay vertically aligned across a row of fields. Unset = natural height. */
  @Input() labelLines: number = null;
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() errorMessage = '';
  /** How errorMessage is shown: 'inline' below the field (default, reserves layout space) or 'floating' as an overlay balloon that doesn't push content below. */
  @Input() errorDisplay: 'inline' | 'floating' = 'inline';
  /** Red error border without an error message (use errorMessage when you also want text below). */
  @Input() hasError = false;
  @Input() prefixIcon = '';
  @Input() suffixIcon = '';
  @Input() suffixText = '';
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() required = false;
  @Input() maxLength: number;
  @Input() min: number | string;
  @Input() max: number | string;
  @Input() step: number;
  @Input() autocomplete = 'off';
  /** For type="search" only: render as an icon button that expands into the full
   * input on click and collapses back when blurred while empty. Lets a search box
   * occupy minimal space in tight toolbars. No effect on other input types. */
  @Input() collapsible = false;
  /** For type="number" only: set to false to hide the native up/down spinner arrows. */
  @Input() showNumberSpinner = true;

  @Output() suffixIconClick = new EventEmitter<void>();
  @Output() inputBlur = new EventEmitter<void>();
  @Output() inputFocus = new EventEmitter<void>();
  @Output() cleared = new EventEmitter<void>();

  value: any = '';
  showPassword = false;
  /** Whether a collapsible search is currently expanded into its input form. */
  expanded = false;

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  onChange = (_: any) => { };
  onTouched = () => { };

  /** True when a collapsible search should show only its icon trigger (empty + not expanded). */
  get isCollapsed(): boolean {
    return this.collapsible && this.type === 'search' && !this.expanded && !this.value;
  }

  /** Shrinks the host to its icon when collapsed, overriding any consumer-set width. */
  @HostBinding('class.pkt-host-collapsed') get hostCollapsed(): boolean {
    return this.isCollapsed;
  }

  /** Expands a collapsed search and focuses the input on the next tick (after it renders). */
  expand() {
    if (this.disabled) return;
    this.expanded = true;
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  get effectiveType(): string {
    if (this.type === 'password') return this.showPassword ? 'text' : 'password';
    return this.type;
  }

  get resolvedSuffixIcon(): string {
    if (this.type === 'password') return this.showPassword ? 'visibility_off' : 'visibility';
    return this.suffixIcon;
  }

  get hasSuffixAction(): boolean {
    return this.type === 'password' || !!this.suffixIcon;
  }

  onSuffixClick() {
    if (this.type === 'password') this.showPassword = !this.showPassword;
    this.suffixIconClick.emit();
  }

  onClear() {
    this.value = '';
    this.onChange('');
    this.onTouched();
    this.cleared.emit();
    // For collapsible search, keep it open and refocus so the user can retype.
    // It then collapses normally if they click away while it's empty.
    if (this.collapsible && this.type === 'search') {
      this.expanded = true;
      setTimeout(() => this.searchInput?.nativeElement.focus());
    }
  }

  writeValue(value: any) { this.value = value ?? ''; }
  registerOnChange(fn: any) { this.onChange = fn; }
  registerOnTouched(fn: any) { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean) { this.disabled = isDisabled; }

  onInputChange(event: Event) {
    const val = (event.target as HTMLInputElement).value;
    this.value = val;
    this.onChange(this.type === 'number' ? (val === '' ? null : +val) : val);
  }

  onBlur() {
    this.onTouched();
    this.inputBlur.emit();
    // Collapse a collapsible search back to its icon when left empty
    if (this.collapsible && this.type === 'search' && !this.value) {
      this.expanded = false;
    }
  }
  onFocus() { this.inputFocus.emit(); }
}
